// Dependencies
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import React, { FC, ReactElement, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
// Core & Lab
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import Button from '@mui/material/Button';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
// Icons
import PhotoCamera from '@mui/icons-material/PhotoCamera';
// Components
// Actions
import { updatePicturesForm } from './redux/actions';
// Utils
import { PictureContext } from './utils/pictureContext';
// Types
import { AppState } from '../../reducers';
import { EMPTY_PICTURE_FORM, PictureForm } from './objects/picture';
import { useLocation } from 'react-router-dom';
import PictureModalUploadEdit from './pictureModalUploadEdit';
import PictureCategoryLabel from './pictureCategoryLabel';
import localeText from '../../functions/localeText';

const PictureModalCreateTabs: FC<{is_creating?: boolean}> = ({ is_creating }): ReactElement => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const location = useLocation();
    const pictures = useSelector((state: AppState) => state.picture.pictures_form);
    const selection = useSelector((state: AppState) => state.picture.selection);
    const edit = useSelector((state: AppState) => state.manualAccommodation.edit);
    const current_locale = useSelector((state: AppState) => state.locale.current_locale);
    const destination_name_locales = useSelector((state: AppState) => state.destination.destination_name_locales);
    const manual_accommodation = useSelector((state: AppState) => state.manualAccommodation.manual_accommodation);
    const circuit_name_locales = useSelector((state: AppState) => state.circuit.circuit_name_locales);


    const create_alt = useSelector((state: AppState) => state.picture.create_alt);
    const create_credit = useSelector((state: AppState) => state.picture.create_credit);
    const create_description = useSelector((state: AppState) => state.picture.create_description);
    const create_name = useSelector((state: AppState) => state.picture.create_name);
    const create_type = useSelector((state: AppState) => state.picture.create_type);
    const main_locale = useSelector((state: AppState) => state.locale.main_locale);

    const [edit_picture_index, setEditPictureIndex] = React.useState<number | null>(null);
    const [default_empty_picture, setDefaultEmptyPicture] = React.useState<PictureForm>(EMPTY_PICTURE_FORM);

    const default_description = useMemo(() => create_description ?? '', [create_description]);
    const default_alt = useMemo(() => create_alt ?? '', [create_alt]);
    const default_credit = useMemo(() => create_credit ?? '', [create_credit]);

    const default_type = useMemo(() => (manual_accommodation && location.pathname.includes('accommodation')) ? 'HOTEL' : create_type ?? 'PICTURE', [manual_accommodation, location, create_type]);

    const default_name = useMemo(() => {
        let name = '';
        if (create_name) {
            name = create_name;
        } else if (manual_accommodation !== null && location.pathname.includes('accommodation')) {
            name = `${t('shared.picture')} ${edit === 'SITUATION' ? 'situation' : ''} ${manual_accommodation.name}`;
        }
        else if (Object.keys(destination_name_locales).length !== 0 && current_locale !== null && location.pathname.includes('destination')) {
            name = `${t('shared.picture')} ${destination_name_locales[current_locale]}`;
        }
        else if (Object.keys(circuit_name_locales).length !== 0 && current_locale !== null && location.pathname.includes('circuit')) {
            name = `${t('shared.picture')} ${circuit_name_locales[current_locale]}`;
        } else if (location.pathname.includes('vehicle-model')) {
            name = t('shared.vehicle-model-picture');
        } else if (location.pathname.includes('activity')) {
            name = t('shared.activity-picture');
        } else {
            name = t('shared.picture');
        }
        return name;
    }, [manual_accommodation, location, edit, current_locale, destination_name_locales, circuit_name_locales, create_name]);

    useEffect(() => {
        setDefaultEmptyPicture(state => ({ ...state, name: default_name }));
    }, [default_name]);
    useEffect(() => {
        setDefaultEmptyPicture(state => ({ ...state, type: default_type }));
    }, [default_type]);
    useEffect(() => {
        setDefaultEmptyPicture(state => ({ ...state, description: default_description }));
    }, [default_description]);
    useEffect(() => {
        setDefaultEmptyPicture(state => ({ ...state, alt: default_alt }));
    }, [default_alt]);
    useEffect(() => {
        setDefaultEmptyPicture(state => ({ ...state, credit: default_credit }));
    }, [default_credit]);

    useEffect(() => {
        if (location.pathname.includes('picture-list')) {
            dispatch(updatePicturesForm([...pictures].map((p) => ({
                ...p,
                description: default_empty_picture.description,
                alt: default_empty_picture.alt,
                credits: default_empty_picture.credits,
                type: default_empty_picture.type,
                name: default_empty_picture.name,
            }))));
        }
    }, [location, default_empty_picture]);


    const onPictureChange = (event: { target: { files: any; }; }) => {
        const updated_files = [];
        for (let i = 0; i < event.target.files.length; i++) {
            updated_files.push(event.target.files[i]);
            if ((event.target.files[i].size / 1024) / 1024 > 2) {
                enqueueSnackbar(t('errors.file-size-2'), { variant: 'error' });
            }
        }
        dispatch(updatePicturesForm([...pictures, ...updated_files.map((file) => ({ ...default_empty_picture, file }))]));
    };

    const onEditPicture = (index: number) => () => {
        setEditPictureIndex(index);
    };
    const onDeletePicture = (index: number) => () => {
        dispatch(updatePicturesForm(pictures.filter((_, i) => i !== index)));
    };
    return (<>
        <PictureContext.Consumer>
            {({ singleFile }) => (
                <Grid container spacing={2}>
                    {
                        pictures?.map((picture, file_index: number) => (
                            <Grid item key={file_index} xs={4}>
                                <Card>
                                    <CardMedia component={'img'} alt={''} height={200} image={URL.createObjectURL(picture?.file)} />
                                    <CardContent>
                                        <Typography>{localeText(main_locale, picture.localization ?? [], picture.name ?? '-', 'name')}</Typography>
                                        <Typography className={'ft-redText'}>{(picture.file?.size / 1024) / 1024 > 2 ? t('errors.file-size-2') : ''}</Typography>
                                        <PictureCategoryLabel picture={ picture }/>
                                    </CardContent>
                                    <CardActions>
                                        {!is_creating && <Button onClick={onEditPicture(file_index)}>{t('shared.update-picture')}</Button>}
                                        <Button onClick={onDeletePicture(file_index)}>{t('shared.delete-picture')}</Button>
                                    </CardActions>
                                </Card>
                            </Grid>
                        ))
                    }
                    <Grid item xs={12}>
                        <input accept={'image/*'} className={'ft-user-picture-input'} id={'icon-button-file'} type={'file'} multiple={!singleFile} onChange={onPictureChange} />
                        {
                            (!singleFile || (pictures?.length === 0 && selection.length === 0)) &&
                            <label htmlFor={'icon-button-file'}>
                                <Button className={'ft-user-picture-button'} startIcon={<PhotoCamera className={'ft-user-picture-icon'} />} component={'span'}>
                                    {t(singleFile ? 'shared.select-picture' : 'shared.select-picture(s)')} {t('shared.max-picture-size')}
                                </Button>
                            </label>
                        }
                    </Grid>
                </Grid>
            )}
        </PictureContext.Consumer>
        <PictureModalUploadEdit picture_index={edit_picture_index} setEditPictureIndex={setEditPictureIndex} />
    </>
    );
};

export default PictureModalCreateTabs;